import { AppToastOutlet, useAppToastRef } from '../providers/app-toast-provider';
import { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Avatar } from 'primereact/avatar';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { Divider } from 'primereact/divider';
import { getUserProfile, UserProfile } from '../general/user';
import { UserDto } from '../general/dtos';
import { useUserStore } from '../components/zustand-stores';
import { Api } from '../general/api';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AxiosError } from 'axios';
import { GeneralErrorDialog } from '../components/dialogs/error-dialog';
import { useApi } from '../providers/api-provider';
import { IUser, useAuth } from '../providers/auth-provider';

export default function ProfilePage() {
  const auth = useAuth();
  const api: Api = useApi();
  const userDto: UserDto | null = useUserStore((s) => s.user);
  const user: IUser | undefined = auth.getUser();
  const effectiveProfile: UserProfile = userDto ? { firstName: userDto.first_name, lastName: userDto.last_name, avatar: userDto.picture} : getUserProfile(user, '');

  const [fileUploadKey, setFileUploadKey] = useState(0);
  const [form, setForm] = useState<UserProfile>(effectiveProfile);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');
  const toast = useAppToastRef();

  const handleTestFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value.length <= 200) {
      setForm({ ...form, [name]: value });
    }
  };

  const handleAvatarSelect = (event: FileUploadSelectEvent) => {
    const input = event.originalEvent?.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) return;

    const reset = () => {
      setFileUploadKey(prev => prev + 1); // force re-render
    };

    const validTypes = ['image/jpeg', 'image/png', 'image/bmp'];
    if (!validTypes.includes(file.type)) {
      toast.current?.show({ severity: 'error', summary: 'Invalid format', detail: 'Only JPG, PNG, BMP allowed' });
      reset();
      return;
    }

    if (file.size > 50 * 1024) {
      toast.current?.show({ severity: 'warn', summary: 'File too large', detail: 'Max size is 50KB' });
      reset();
      return;
    }

    const img = new Image();
    img.onload = () => {
      if (img.width > 200 || img.height > 200) {
        toast.current?.show({ severity: 'warn', summary: 'Image too large', detail: 'Max 200x200 pixels' });
        reset();
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          setForm({ ...form, avatar: reader.result as string });
          reset();
        };
        reader.readAsDataURL(file);
      }
    };
    img.onerror = () => {
      toast.current?.show({ severity: 'error', summary: 'Image load failed', detail: 'Could not read image' });
      reset();
    };
    img.src = URL.createObjectURL(file);
  };

  const handleSubmit = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.current?.show({ severity: 'error', summary: 'Validation error', detail: 'First and Last Name are required' });
      return;
    }

    try {
      setLoading(true);
      try {
        const result: UserDto | null = await api.updateMyProfile(form);
        if (result) {
            useUserStore.getState().setUser(result);
        }
      } catch (error: AxiosError | any) {
        const message: string = error.response?.data?.message ?? 'Update error';
        setError(message);
        setErrorTitle('Error updating your profile information');
        setErrorDetails('There was an unhandled error while trying to update the user profile. Please try again later or contact support :)');
        return;
      }

      toast.current?.show({ severity: 'success', summary: 'Profile updated', detail: 'Changes saved successfully' });
    } finally {
      setLoading(false);
    }
  };

  return (<>
    <div className="p-4 max-w-xl mx-auto">
      <AppToastOutlet toastRef={toast} />
      <Card title="Edit profile" className="shadow-2">
        <div className="flex flex-column align-items-center mb-4">
          <Avatar image={form.avatar} size="xlarge" shape="circle" className="mb-3" />
          <span className="text-sm text-color-secondary">Live avatar preview</span>
        </div>

        <div className="p-fluid">
          <div className="formgrid grid">
            <div className="field col-12 md:col-6">
              <label htmlFor="firstName">First name*</label>
              <InputText
                id="firstName"
                name="firstName"
                value={form.firstName}
                onChange={handleTestFieldChange}
                maxLength={200}
                required
              />
            </div>

            <div className="field col-12 md:col-6">
              <label htmlFor="lastName">Last name*</label>
              <InputText
                id="lastName"
                name="lastName"
                value={form.lastName}
                onChange={handleTestFieldChange}
                maxLength={200}
                required
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="avatar">Upload avatar (JPG, PNG, BMP, ≤50KB, ≤200×200)</label>
            <FileUpload
              key={fileUploadKey}
              mode="basic"
              name="avatar"
              accept=".jpg,.jpeg,.png,.bmp"
              maxFileSize={50 * 1024}
              customUpload={false}
              auto
              chooseLabel="Upload avatar"
              onSelect={handleAvatarSelect}
            />
          </div>
        </div>

        <Divider />
        <div style={{ textAlign: 'center' }}>
          <Button
            label="Save changes"
            icon="pi pi-save"
            loading={loading}
            onClick={handleSubmit}
          />
        </div>
      </Card>
    </div>

    <GeneralErrorDialog
      visible={error.length > 0}
      title={errorTitle}
      message={error}
      details={errorDetails}
      onClose={() => setError('')}
    />
  </>);
}
